// Spec §F-4 (mvp-delivery/02-flow-specification.md L884) — Reschedule UI:
//   Шинэ хүргэх огноо: <date>
//   Шинэ жолооч:      <dropdown>
//   Шинэ машин:       <dropdown>
//   Шилжүүлэх шалтгаан: <textarea, required>
// Original UI asked the manager to paste a UUID by hand — replaced here
// with searchable Select fed from /ord/staff/select and /ord/vehicle/list.

import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { nameOptionsFilter } from "@dehub/theme"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import * as StaffApi from "../../apis/staff"
import * as VehicleApi from "../../apis/vehicle"
import type { IAuth } from "../../store/Auth"
import { renderFullName } from "../../utils/render-name"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { useFailedMutations } from "./hooks"

interface Props {
  opened: boolean
  onClose: () => void
  deliveryNoteId: string
  previousExceptionId?: string | null
  /** Optional current assignment shown as context. */
  currentStaffName?: string | null
  currentVehiclePlate?: string | null
  currentScheduledDate?: string | null
}

const REASON_MIN = 3

export function ReassignDialog({
  opened,
  onClose,
  deliveryNoteId,
  previousExceptionId,
  currentStaffName,
  currentVehiclePlate,
  currentScheduledDate,
}: Props) {
  const { t } = useTranslation("delivery")
  const mutations = useFailedMutations()
  const reassignState = useDeliveryActions().permissionState("reassign")
  const businessId = useSelector(
    (s: { auth: IAuth }) => s.auth.user?.currentBusinessId ?? null,
  )

  const [toDriverId, setToDriverId] = useState<string | null>(null)
  const [toVehicleId, setToVehicleId] = useState<string | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [reason, setReason] = useState("")

  const [driverOptions, setDriverOptions] = useState<{ value: string; label: string }[]>([])
  const [driversLoading, setDriversLoading] = useState(false)
  const [vehicleOptions, setVehicleOptions] = useState<{ value: string; label: string }[]>([])
  const [vehiclesLoading, setVehiclesLoading] = useState(false)

  // Lazy-load drivers + vehicles only when the dialog opens, so list views
  // don't pay the cost upfront.
  useEffect(() => {
    if (!opened || !businessId) return
    let cancelled = false
    setDriversLoading(true)
    setVehiclesLoading(true)
    StaffApi.select({ businessId, limit: 100 })
      .then((rows) => {
        if (cancelled) return
        setDriverOptions(
          rows.map((r) => ({
            value: r.id,
            label: (r.lastName || r.firstName ? renderFullName(r) : "") || r.id,
          })),
        )
      })
      .catch(() => { if (!cancelled) setDriverOptions([]) })
      .finally(() => { if (!cancelled) setDriversLoading(false) })

    VehicleApi.list({ filter: { isActive: true }, offset: { limit: 100, skip: 0 } })
      .then((res) => {
        if (cancelled) return
        setVehicleOptions(
          res.rows.map((v) => ({
            value: v.id,
            label: [v.plateNumber, [v.make, v.model].filter(Boolean).join(" ")]
              .filter(Boolean).join(" · ") || v.id,
          })),
        )
      })
      .catch(() => { if (!cancelled) setVehicleOptions([]) })
      .finally(() => { if (!cancelled) setVehiclesLoading(false) })

    return () => { cancelled = true }
  }, [opened, businessId])

  const minDate = useMemo(() => dayjs().startOf("day").toDate(), [])

  const handleClose = () => {
    setToDriverId(null)
    setToVehicleId(null)
    setToDate(null)
    setReason("")
    onClose()
  }

  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= REASON_MIN
  // Spec §F-4: at least one of (driver, vehicle, date) must change, otherwise
  // there is nothing to reassign. Reason is always required.
  const hasAnyChange = !!(toDriverId || toVehicleId || toDate)
  const isValid = reasonValid && hasAnyChange

  const handleSubmit = async () => {
    if (!isValid) return
    await mutations.reassign(deliveryNoteId, {
      toDriverId: toDriverId ?? null,
      toVehicleId: toVehicleId ?? null,
      toScheduledDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
      reason: trimmedReason,
      previousExceptionId: previousExceptionId ?? null,
    })
    handleClose()
  }

  const hasCurrent = !!(currentStaffName || currentVehiclePlate || currentScheduledDate)

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={650} fz="var(--nw-fz-drawer)">
          {t("reassignDlg.title")}
        </Text>
      }
      size="md"
    >
      <Stack gap="md">
        {mutations.error && (
          <Alert color="red" variant="light">
            {translateBackendError(mutations.error)}
          </Alert>
        )}

        {hasCurrent && (
          <Box
            p="sm"
            style={{
              background: "var(--mantine-color-gray-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-gray-2)",
            }}
          >
            <Text size="sm" c="dimmed" mb={6}>
              {t("reassignDlg.currentSchedule")}
            </Text>
            <Stack gap={2}>
              {currentScheduledDate && (
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed" w={70}>{t("reassignDlg.date")}</Text>
                  <Text size="sm" fw={500}>
                    {dayjs(currentScheduledDate).format("YYYY-MM-DD")}
                  </Text>
                </Group>
              )}
              {currentStaffName && (
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed" w={70}>{t("reassignDlg.driver")}</Text>
                  <Text size="sm" fw={500}>{currentStaffName}</Text>
                </Group>
              )}
              {currentVehiclePlate && (
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" c="dimmed" w={70}>{t("reassignDlg.vehicle")}</Text>
                  <Text size="sm" fw={500}>{currentVehiclePlate}</Text>
                </Group>
              )}
            </Stack>
          </Box>
        )}

        <DatePickerInput
          label={t("reassignDlg.newDate")}
          placeholder={t("reassignDlg.selectDate")}
          value={toDate}
          onChange={(v) => setToDate(v ? new Date(v as unknown as string) : null)}
          minDate={minDate}
          clearable
          valueFormat="YYYY-MM-DD"
        />

        <Select
          label={t("reassignDlg.newDriver")}
          placeholder={driversLoading ? t("reassignDlg.loading") : t("reassignDlg.selectDriver")}
          data={driverOptions}
          value={toDriverId}
          onChange={setToDriverId}
          searchable
          filter={nameOptionsFilter}
          clearable
          nothingFoundMessage={t("reassignDlg.notFound")}
          disabled={driversLoading}
        />

        <Select
          label={t("reassignDlg.newVehicle")}
          placeholder={vehiclesLoading ? t("reassignDlg.loading") : t("reassignDlg.selectVehicle")}
          data={vehicleOptions}
          value={toVehicleId}
          onChange={setToVehicleId}
          searchable
          clearable
          nothingFoundMessage={t("reassignDlg.notFound")}
          disabled={vehiclesLoading}
        />

        <Textarea
          label={t("reassignDlg.reasonLabel")}
          description={t("reassignDlg.reasonDesc")}
          placeholder={t("reassignDlg.reasonPlaceholder")}
          required
          minRows={3}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={
            reason.length > 0 && !reasonValid
              ? t("reassignDlg.reasonMinError", { n: REASON_MIN })
              : undefined
          }
        />

        {!hasAnyChange && (
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("reassignDlg.noChangeHint")}
          </Text>
        )}

        <Stack gap={4} align="flex-end">
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={mutations.submitting}>
              {t("reassignDlg.cancel")}
            </Button>
            {/* O09/O113: DEL_CHANNEL_OVERRIDE (BE failed-reassign.ts); shown with the reason, never hidden. */}
            <DeliveryActionButton
              state={reassignState}
              color="dark"
              onClick={handleSubmit}
              loading={mutations.submitting}
              disabled={!isValid}
            >
              {t("reassignDlg.confirm")}
            </DeliveryActionButton>
          </Group>
          {!reassignState.allowed && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {reassignState.reason}
            </Text>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
